import { useRef, useState, useCallback, useEffect } from "react";

type SnapCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface Position {
  x: number;
  y: number;
}

interface UseDraggableOptions {
  edgeMargin?: number;
  mobileBottomMargin?: number;
  isMobile?: boolean;
}

const DRAG_THRESHOLD = 5;

const useDraggable = (options?: UseDraggableOptions) => {
  const edgeMargin = options?.edgeMargin ?? 24;
  const mobileBottomMargin = options?.mobileBottomMargin ?? 80;
  const isMobile = options?.isMobile ?? false;

  const [corner, setCorner] = useState<SnapCorner>("bottom-right");
  const [draggingPos, setDraggingPos] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    elX: number;
    elY: number;
    pointerId: number;
    captured: boolean;
  } | null>(null);
  const hasDraggedRef = useRef(false);

  const getCornerPosition = useCallback(
    (c: SnapCorner): Record<string, number | string> => {
      const bottomMargin = isMobile ? mobileBottomMargin : edgeMargin;
      const sideMargin = isMobile ? 16 : edgeMargin;
      switch (c) {
        case "top-left":
          return { top: edgeMargin, left: sideMargin, bottom: "auto", right: "auto" };
        case "top-right":
          return { top: edgeMargin, right: sideMargin, bottom: "auto", left: "auto" };
        case "bottom-left":
          return { bottom: bottomMargin, left: sideMargin, top: "auto", right: "auto" };
        case "bottom-right":
          return { bottom: bottomMargin, right: sideMargin, top: "auto", left: "auto" };
      }
    },
    [edgeMargin, mobileBottomMargin, isMobile]
  );

  const findNearestCorner = useCallback(
    (x: number, y: number): SnapCorner => {
      const el = dragRef.current;
      const w = el ? el.getBoundingClientRect().width : 0;
      const h = el ? el.getBoundingClientRect().height : 0;
      const centerX = x + w / 2;
      const centerY = y + h / 2;
      const midX = window.innerWidth / 2;
      const midY = window.innerHeight / 2;

      if (centerX <= midX && centerY <= midY) return "top-left";
      if (centerX > midX && centerY <= midY) return "top-right";
      if (centerX <= midX && centerY > midY) return "bottom-left";
      return "bottom-right";
    },
    []
  );

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const el = dragRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      elX: rect.left,
      elY: rect.top,
      pointerId: e.pointerId,
      captured: false,
    };
    hasDraggedRef.current = false;
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const start = dragStartRef.current;
    const el = dragRef.current;
    if (!start || !el) return;

    const dx = e.clientX - start.mouseX;
    const dy = e.clientY - start.mouseY;

    // Don't start dragging until threshold is crossed
    if (!hasDraggedRef.current) {
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
        return;
      }
      hasDraggedRef.current = true;
      setIsDragging(true);
      // Capture pointer only once we know it's a real drag
      if (!start.captured) {
        el.setPointerCapture(start.pointerId);
        start.captured = true;
      }
    }

    const elRect = el.getBoundingClientRect();
    const newX = Math.max(
      0,
      Math.min(window.innerWidth - elRect.width, start.elX + dx)
    );
    const newY = Math.max(
      0,
      Math.min(window.innerHeight - elRect.height, start.elY + dy)
    );

    setDraggingPos({ x: newX, y: newY });
  }, []);

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const start = dragStartRef.current;
      if (start?.captured && dragRef.current) {
        dragRef.current.releasePointerCapture(e.pointerId);
      }
      if (hasDraggedRef.current && draggingPos) {
        const nearest = findNearestCorner(draggingPos.x, draggingPos.y);
        setCorner(nearest);
      }
      setDraggingPos(null);
      dragStartRef.current = null;
      setIsDragging(false);
    },
    [draggingPos, findNearestCorner]
  );

  useEffect(() => {
    const handleResize = () => setDraggingPos(null);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dragHandlers = {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
  };

  const positionStyles: Record<string, number | string> = draggingPos
    ? {
        left: draggingPos.x,
        top: draggingPos.y,
        right: "auto",
        bottom: "auto",
        transition: "none",
      }
    : {
        ...getCornerPosition(corner),
        transition: "all 0.3s cubic-bezier(0.25, 1, 0.5, 1)",
      };

  return {
    dragRef,
    isDragging,
    hasDragged: hasDraggedRef,
    corner,
    dragHandlers,
    positionStyles,
  };
};

export default useDraggable;
